import React, { useMemo, useRef } from "react";
import Plot from "../../graphViewV2/charts/PlotlyChart";
import {
  plotlyAxisBase,
  plotlyAxisTitle,
  plotlyAxisBaseCategoricalX,
} from "../../graphViewV2/charts/plotlyAxisTheme";
import { plotlyHorizontalLegendForTraces } from "../../graphViewV2/charts/plotlyLegendLayout";
import {
  GraphV2FullscreenInlineRow,
  POND_STAT_DESKTOP_HEIGHT,
  POND_STAT_MOBILE_HEIGHT,
  useGraphV2PlotHeight,
} from "../../graphViewV2/ChartFullscreenShell";
import { getPlotlyLegendStyle } from "../../graphView/colors";
import { LABEL } from "../../../../utils/samplingDisplayLabels";

/**
 * @param {boolean} isMobile
 * @param {string} binUnitLabel
 * @param {Array<{ dateLabel: string, binLabels: string[], cumulative: number[] }>} props.seriesList
 */
export default function PondStatCumulativeLinesByDateChart({ isMobile, binUnitLabel, seriesList }) {
  const plotRef = useRef(null);

  const binLabels = useMemo(() => {
    const longest = (seriesList || []).reduce(
      (best, s) => (s.binLabels && s.binLabels.length > (best?.length || 0) ? s.binLabels : best),
      []
    );
    return longest || [];
  }, [seriesList]);

  const traces = useMemo(() => {
    const labels = (seriesList || []).map((s) => s.dateLabel || "Session");
    return (seriesList || []).map((s, idx) => {
      const name = s.dateLabel || `Session ${idx + 1}`;
      const { color, symbol } = getPlotlyLegendStyle(name, labels);
      const yByBin = new Map((s.binLabels || []).map((b, i) => [b, s.cumulative[i]]));
      const y = binLabels.map((b) => {
        const v = yByBin.get(b);
        return v == null || !Number.isFinite(v) ? null : v;
      });
      return {
        type: "scatter",
        mode: "lines+markers",
        name,
        x: binLabels,
        y,
        line: { color, width: 2 },
        marker: { color, size: 6, symbol, line: { width: 1, color: "white" } },
        connectgaps: false,
      };
    });
  }, [seriesList, binLabels]);

  const legendLayout = useMemo(
    () => plotlyHorizontalLegendForTraces(traces, isMobile, { itemsPerRow: 5 }),
    [traces.length, isMobile]
  );

  const plotHeight = useGraphV2PlotHeight({
    isMobile,
    mobileDefault: POND_STAT_MOBILE_HEIGHT,
    desktopDefault: POND_STAT_DESKTOP_HEIGHT,
    legendExtra: legendLayout.extraHeight || 0,
  });

  if (!seriesList || seriesList.length === 0 || !binLabels.length) {
    return (
      <p className="text-muted text-center py-6 mb-0">No cumulative-by-date data.</p>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <GraphV2FullscreenInlineRow />
      <Plot
          ref={plotRef}
          data={traces}
          layout={{
            autosize: true,
            height: plotHeight,
            margin: { t: legendLayout.marginTop, r: 48, b: 72, l: 56 },
            paper_bgcolor: "white",
            plot_bgcolor: "white",
            xaxis: {
              type: "category",
              categoryorder: "array",
              categoryarray: binLabels,
              title: plotlyAxisTitle(`Bin size (${binUnitLabel})`, isMobile),
              ...plotlyAxisBaseCategoricalX(isMobile, "default"),
            },
            yaxis: {
              title: plotlyAxisTitle(LABEL.CUM_PCT_OF_POPULATION, isMobile),
              range: [0, 100],
              ...plotlyAxisBase(isMobile, "default"),
            },
            legend: legendLayout.legend,
            hovermode: "closest",
          }}
          config={{ responsive: true, displaylogo: false }}
          style={{ width: "100%" }}
          useResizeHandler
        />
    </div>
  );
}
